import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  SecureStorage,
  KeychainAccess
} from '@aparajita/capacitor-secure-storage';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const DEVICE_TOKEN_KEY =
  'veritas_instructor_device_token';


function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json'
  };
}


async function parse(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${
        text || response.statusText
      }`
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


function toSession(instructor, token) {
  if (
    !instructor?.id ||
    !instructor?.email ||
    !token
  ) {
    return null;
  }

  return {
    instructorId: instructor.id,
    email: instructor.email,
    name: instructor.name || '',
    isSuperadmin:
      instructor.is_superadmin === true,
    cmsToken: token,
    nativeDeviceSession: true,
    expiresAt:
      Date.now() +
      30 * 24 * 60 * 60 * 1000
  };
}


async function saveToken(token) {
  await SecureStorage.set(
    DEVICE_TOKEN_KEY,
    token,
    true,
    false,
    KeychainAccess.whenUnlockedThisDeviceOnly
  );
}


async function readToken() {
  const value =
    await SecureStorage.get(
      DEVICE_TOKEN_KEY,
      true,
      false
    );

  return (
    typeof value === 'string' &&
    value.trim()
  )
    ? value.trim()
    : null;
}


async function removeToken() {
  try {
    await SecureStorage.remove(
      DEVICE_TOKEN_KEY,
      false
    );
  } catch (err) {
    console.error(
      'Removing instructor device token failed:',
      err
    );
  }
}


function buildDeviceName(info, platform) {
  const manufacturer =
    String(info?.manufacturer || '').trim();

  const model =
    String(info?.model || '').trim();

  const parts = [];

  if (
    manufacturer &&
    !model
      .toLowerCase()
      .startsWith(
        manufacturer.toLowerCase()
      )
  ) {
    parts.push(manufacturer);
  }

  if (model) {
    parts.push(model);
  }

  let name =
    parts.join(' ').trim();

  if (!name) {
    name =
      platform === 'ios'
        ? 'iPhone / iPad'
        : platform === 'android'
          ? 'Android device'
          : 'Mobile device';
  }

  if (info?.isVirtual) {
    name += ' (Emulator)';
  }

  return name;
}


async function getDeviceRegistrationName(
  platform
) {
  try {
    const info =
      await Device.getInfo();

    return buildDeviceName(
      info,
      platform
    );
  } catch (err) {
    console.error(
      'Reading instructor device information failed:',
      err
    );

    return platform === 'ios'
      ? 'iPhone / iPad'
      : platform === 'android'
        ? 'Android device'
        : 'Mobile device';
  }
}


async function revokeTokenOnServer(token) {
  if (!token) return;

  try {
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/revoke_instructor_device`,
      {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          p_token: token
        })
      }
    );
  } catch (err) {
    console.error(
      'Revoking instructor device token failed:',
      err
    );
  }
}


export async function getInstructorDeviceToken() {
  return readToken();
}


export async function registerInstructorDeviceSession(
  cmsToken
) {
  const platform =
    Capacitor.getPlatform();

  const deviceName =
    await getDeviceRegistrationName(
      platform
    );

  let previousToken = null;

  try {
    previousToken =
      await readToken();
  } catch (_) {
    // A missing/old token must not block a fresh
    // OTP-authenticated registration.
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/register_instructor_device`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_cms_token: cmsToken,
        p_device_name: deviceName,
        p_platform: platform
      })
    }
  );

  const result =
    await parse(
      response,
      'Registering this instructor device'
    );

  if (
    !result?.ok ||
    !result?.token ||
    !result?.instructor
  ) {
    throw new Error(
      result?.message ||
        'This device could not be registered for saved login.'
    );
  }

  try {
    await saveToken(
      result.token
    );
  } catch (err) {
    await revokeTokenOnServer(
      result.token
    );

    throw new Error(
      'Secure device storage is unavailable on this device.'
    );
  }

  // Re-registering the same physical phone should
  // not leave its old saved-login token active.
  if (
    previousToken &&
    previousToken !== result.token
  ) {
    await revokeTokenOnServer(
      previousToken
    );
  }

  localStorage.removeItem(
    'instructorMobileSession'
  );

  return toSession(
    result.instructor,
    result.token
  );
}


export async function restoreInstructorDeviceSession() {
  let token;

  try {
    token =
      await readToken();
  } catch (err) {
    throw new Error(
      'The app could not read its saved login from secure device storage.'
    );
  }

  if (!token) {
    return null;
  }

  let response;

  try {
    response = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/instructor_session_from_device`,
      {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          p_token: token
        })
      }
    );
  } catch (err) {
    throw new Error(
      'The app could not connect to verify the saved login. Check your internet connection and try again.'
    );
  }

  const result =
    await parse(
      response,
      'Restoring instructor saved login'
    );

  if (
    !result?.ok ||
    !result?.instructor
  ) {
    await removeToken();
    return null;
  }

  return toSession(
    result.instructor,
    token
  );
}


export async function revokeInstructorDeviceSession() {
  let token = null;

  try {
    token =
      await readToken();
  } catch (_) {
    // Continue clearing local secure storage.
  }

  if (token) {
    await revokeTokenOnServer(
      token
    );
  }

  await removeToken();

  localStorage.removeItem(
    'instructorMobileSession'
  );
}